import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { activityService } from '../services/api';
import {
  ClipboardList, Loader2, RefreshCw, ChevronRight, Lock, Zap, Plus, X,
  CheckCircle, XCircle, Clock,
} from 'lucide-react';

const PRIORITY_STYLES = { high: 'bg-rose-100 text-rose-800', medium: 'bg-amber-100 text-amber-800', low: 'bg-gray-100 text-gray-700' };
const STATUS_STYLES = { open: 'bg-blue-100 text-blue-800', done: 'bg-green-100 text-green-800', dismissed: 'bg-gray-200 text-gray-600' };
const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString() : '—'; } catch { return '—'; } };

const ActivitiesPage = () => {
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const [list, s] = await Promise.all([activityService.listActivities(params), activityService.getStats()]);
      setData(list); setStats(s); setDisabled(false);
    } catch (e) {
      if (e?.response?.status === 403) setDisabled(true);
      else setError('Failed to load activities.');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true); setGenMsg(null);
    try {
      const r = await activityService.generate();
      setGenMsg(`Generated ${r.created} new activity(ies); ${r.skipped_existing} already open.`);
      await load();
    } catch (e) { setGenMsg(e?.response?.data?.detail || 'Generation failed.'); }
    finally { setGenerating(false); }
  };

  const setStatus = async (id, status) => {
    setBusy(id); setError(null);
    try { await activityService.updateActivity(id, { status }); await load(); }
    catch (e) { setError(e?.response?.data?.detail || `Could not mark activity ${status}.`); }
    finally { setBusy(null); }
  };

  if (disabled) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto text-center py-20" data-testid="activities-disabled-notice">
          <Lock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Follow-Up Activities is not active</h2>
          <p className="text-gray-600 mt-2">Enable the <code className="text-sm bg-gray-100 px-1 rounded">follow_up_activities_enabled</code> feature flag (Soft Go-Live) to use the activity engine.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="activities-page">
        <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
          <Link to="/admin/dashboard" className="hover:text-gray-700" data-testid="activities-breadcrumb-home">Admin Panel</Link>
          <ChevronRight className="w-4 h-4 mx-1" /><span className="text-gray-900 font-medium">Follow-Up Activities</span>
        </nav>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><ClipboardList className="w-7 h-7 text-blue-600" /> Follow-Up Activities</h1>
            <p className="text-gray-600 mt-1">Actionable follow-ups generated from lead &amp; quote events.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={generate} disabled={generating} data-testid="activities-generate-btn" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Generate</button>
            <button onClick={() => setShowNew(true)} data-testid="activities-new-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"><Plus className="w-4 h-4" /> New</button>
            <button onClick={load} data-testid="activities-refresh-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>

        {genMsg && <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-3 text-sm" data-testid="activities-gen-msg">{genMsg}</div>}
        {error && <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">{error}</div>}

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="activities-stats">
            <div className="bg-white border border-gray-200 rounded-lg p-3"><p className="text-xs text-gray-500">Total</p><p className="text-2xl font-bold text-gray-900">{stats.total}</p></div>
            <div className="bg-white border border-gray-200 rounded-lg p-3"><p className="text-xs text-gray-500">Open</p><p className="text-2xl font-bold text-blue-600" data-testid="activities-open-count">{stats.open}</p></div>
            {(stats.by_status || []).filter((r) => r.key === 'done' || r.key === 'dismissed').map((r) => (
              <div key={r.key} className="bg-white border border-gray-200 rounded-lg p-3"><p className="text-xs text-gray-500 capitalize">{r.key}</p><p className="text-2xl font-bold text-gray-900">{r.count}</p></div>
            ))}
          </div>
        )}

        <div className="flex gap-2" data-testid="activities-filters">
          {['open', 'done', 'dismissed', ''].map((s) => (
            <button key={s || 'all'} onClick={() => setStatusFilter(s)} data-testid={`activities-filter-${s || 'all'}`} className={`px-3 py-1.5 text-sm rounded-md border ${statusFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>{s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}</button>
          ))}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16" data-testid="activities-loading"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
          ) : data.items.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-center px-6" data-testid="activities-empty">
              <ClipboardList className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-gray-700 font-medium">No activities</p>
              <p className="text-gray-500 text-sm mt-1">Click Generate to create follow-ups from current leads &amp; quotes.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100" data-testid="activities-list">
              {data.items.map((a) => (
                <li key={a.id} data-testid={`activity-row-${a.id}`} className="p-4 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[a.status]}`}>{a.status}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${PRIORITY_STYLES[a.priority] || PRIORITY_STYLES.medium}`}>{a.priority}</span>
                      <span className="font-medium text-gray-900">{a.title}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{a.related_label || '—'}{a.due_at ? ` · due ${fmtDate(a.due_at)}` : ''}{a.source === 'manual' ? ' · manual' : ''}</p>
                    {a.note && <p className="text-xs text-gray-600 mt-1">{a.note}</p>}
                  </div>
                  {a.status === 'open' && (
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => setStatus(a.id, 'done')} disabled={busy === a.id} data-testid={`activity-done-${a.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"><CheckCircle className="w-3 h-3" /> Done</button>
                      <button onClick={() => setStatus(a.id, 'dismissed')} disabled={busy === a.id} data-testid={`activity-dismiss-${a.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"><XCircle className="w-3 h-3" /> Dismiss</button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showNew && <NewActivity onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </DashboardLayout>
  );
};

const NewActivity = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', due_at: '', related_label: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const create = async (e) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try { await activityService.createActivity({ ...form, type: 'manual', due_at: form.due_at || null }); onCreated(); }
    catch (e2) { setErr(e2?.response?.data?.detail || 'Create failed.'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="activity-new-modal" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={create}>
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Clock className="w-5 h-5 text-blue-600" /> New Activity</h2>
            <button type="button" onClick={onClose} aria-label="Close"><X className="w-5 h-5 text-gray-400" /></button>
          </div>
          <div className="p-4 space-y-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="na-title">Title *</label><input id="na-title" required value={form.title} onChange={upd('title')} data-testid="activity-new-title" placeholder="e.g. Customer callback scheduled" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="na-desc">Description</label><textarea id="na-desc" rows={2} value={form.description} onChange={upd('description')} data-testid="activity-new-desc" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="na-pri">Priority</label><select id="na-pri" value={form.priority} onChange={upd('priority')} data-testid="activity-new-priority" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="na-due">Due date</label><input id="na-due" type="date" value={form.due_at} onChange={upd('due_at')} data-testid="activity-new-due" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
            </div>
            {err && <p role="alert" className="text-sm text-red-600">{err}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving} data-testid="activity-new-submit" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Create</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActivitiesPage;
